import React, { useEffect, useState } from 'react';
import Drawer from 'react-modern-drawer';
import { useAuth } from 'app/modules/auth';
import db from 'services/firebase';
import { collection, doc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore';
import DrawerHeader from 'app/components/Drawer/DrawerHeader';
import StatusBadge from 'app/components/StatusBadge';
import { Link } from 'react-router-dom';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';
import { AccessReviewData, OwnerData, ViewUserProps } from '../../utils/DrawersUtils';
import FormatTimestamp from 'app/components/FormatTimestamp';
import OwnerDisplay from 'app/components/OwnerDisplay';

interface ReviewData {
  reviewName: string;
  systems: { [key: string]: { name: string; icon: string; domain: string; riskScore: string } };
  dueDate: string;
  reviewer: { id: string; name: string; email: string };
  status: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
}

interface DisplayReview {
  id: string;
  reviewName: string;
  systemNames: string;
  systemLogos: string;
  domains: string;
  dueDate: string;
  reviewer: { id: string; name: string; email: string };
  riskScores: string;
  integrationStatuses: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
}

interface PlatformData {
  platform: string;
  createdAt: Timestamp;
  status: string;
}

const formatTimestamp = (timestamp: Timestamp): string => {
  try {
    const date = timestamp.toDate();
    return (
      date
        .toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: true,
          timeZone: 'UTC',
          timeZoneName: 'short',
        })
        .replace(',', '') + ' UTC+5:45'
    );
  } catch (error) {
    return '--';
  }
};

const ViewUser: React.FC<ViewUserProps> = ({ isOpen, onClose, id }) => {
  const { currentUser } = useAuth();
  const [accessReview, setAccessReview] = useState<AccessReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [owners, setOwners] = useState<any[]>([]);
  const [owner, setOwner] = useState<OwnerData | null>(null);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('reviews');
  const [reviews, setReviews] = useState<DisplayReview[]>([]);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [linkedPlatforms, setLinkedPlatforms] = useState<PlatformData[]>([]);
  const [platformsError, setPlatformsError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !currentUser?.tenantId) {
      setError('Invalid ID or tenant ID not found.');
      setLoading(false);
      return;
    }
    const [platform, ...uidParts] = id.split('-');
    if (!platform || !uidParts.length) {
      setError('Invalid ID format. Expected format: platform-uid');
      setLoading(false);
      return;
    }
    const uid = uidParts.join('-');
    const fetchAccessReview = () => {
      const tenantRef = doc(db, 'tenants', currentUser.tenantId!);
      const platformDocName = platform.toLowerCase();
      const accessReviewRef = doc(tenantRef, 'access_review', platformDocName);

      const unsubscribe = onSnapshot(
        accessReviewRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            const members = data.members || {};
            const userData = members[uid];

            if (userData) {
              const ownerData = userData.owner || null;
              setAccessReview({
                id: id,
                platform: userData.platform || platform,
                tenantId: userData.tenantId || currentUser.tenantId,
                uid: userData.accountId || uid,
                name: userData.name || '',
                createdAt: userData.createdAt ? formatTimestamp( userData.createdAt ) : '',
                updatedAt: userData.updatedAt ? formatTimestamp(userData.updatedAt) : '',
                workspaceId: userData.workspaceId || '',
                owner: ownerData,
                status: userData.status || 'Active',
                role: userData.accountRole || '',
                twoFactorEnabled: userData.mfaEnabled || false,
                deactivatedOn: userData.deactivatedOn || '',
                accessReviews: userData.accessReviews || [],
              });
              setOwner(ownerData);
              setOwnerError(ownerData ? null : 'Please select an owner.');
            } else {
              setError(`No user data found for UID ${uid} under ${platformDocName}.`);
              setAccessReview(null);
              setOwner(null);
              setOwnerError('Please select an owner.');
            }
          } else {
            setError(`No access review document found for platform ${platformDocName}.`);
            setAccessReview(null);
            setOwner(null);
            setOwnerError('Please select an owner.');
          }
          setLoading(false);
        },
        (error) => {
          setError('Error fetching access review data.');
          setAccessReview(null);
          setOwner(null);
          setOwnerError('Please select an owner.');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    };

    fetchAccessReview();
  }, [id, currentUser]);

  useEffect(() => {
    const tenantId = currentUser?.tenantId || '';
    const tenantRef = doc(db, 'tenants', tenantId);
    const policyRef = collection(tenantRef, 'employees');

    const unsubscribe = onSnapshot(policyRef, (snapshot) => {
      const risksData: any[] = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date);
      setOwners(risksData);
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const getReviews = () => {
      try {
        const tenantId = currentUser?.tenantId || '';
        if (!tenantId) {
          setReviewsError('Tenant ID not found.');
          return;
        }

        const tenantRef = doc(db, 'tenants', tenantId);
        const reviewsRef = collection(tenantRef, 'reviews');

        const unsubscribe = onSnapshot(
          reviewsRef,
          (snapshot) => {
            const allReviews: DisplayReview[] = snapshot.docs.map((doc) => {
              const reviewData = doc.data() as ReviewData;
              const systems = reviewData.systems || {};

              const systemNames = Object.values(systems)
                .map((s) => s.name)
                .join(', ');
              const systemLogos = Object.values(systems)
                .map((s) => s.icon)
                .join(', ');
              const domains = Object.values(systems)
                .map((s) => s.domain)
                .join(', ');
              const riskScores = Object.values(systems)
                .map((s) => s.riskScore)
                .join(', ');

              return {
                id: doc.id,
                reviewName: reviewData.reviewName,
                systemNames,
                systemLogos,
                domains,
                dueDate: reviewData.dueDate,
                reviewer: reviewData.reviewer,
                riskScores,
                integrationStatuses: reviewData.status,
                createdAt: reviewData.createdAt,
                updatedAt: reviewData.updatedAt,
                completedAt: reviewData.completedAt,
              };
            });

            setReviews(allReviews);
            setReviewsError(null);
          },
          (error) => {
            console.error('Error getting reviews:', error);
            setReviewsError('Error fetching reviews.');
          }
        );

        return () => unsubscribe();
      } catch (error) {
        console.error('Error getting reviews:', error);
        setReviewsError('Error fetching reviews.');
      }
    };

    getReviews();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.tenantId || !owner) {
      setLinkedPlatforms([]);
      return;
    }

    const tenantRef = doc(db, 'tenants', currentUser.tenantId);
    const accessReviewRef = collection(tenantRef, 'access_review');

    const unsubscribe = onSnapshot(
      accessReviewRef,
      (snapshot) => {
        const platforms: PlatformData[] = [];
        snapshot.docs.forEach((doc) => {
          const platform = doc.id;
          const data = doc.data();
          const members = data.members || {};

          Object.values(members).forEach((member: any) => {
            if (
              member.owner &&
              member.owner.id === owner.id &&
              member.owner.name === owner.name &&
              member.owner.email === owner.email
            ) {
              platforms.push({
                platform,
                createdAt: member.createdAt || Timestamp.now(),
                status : member.status || 'Active',
              });
            }
          });
        });

        setLinkedPlatforms(platforms);
        setPlatformsError(null);
      },
      (error) => {
        console.error('Error fetching linked platforms:', error);
        setPlatformsError('Error fetching linked platforms.');
        setLinkedPlatforms([]);
      }
    );

    return () => unsubscribe();
  }, [currentUser, owner]);

  const handleOwnerChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOwnerId = e.target.value;
    const selectedOwner = owners.find((owner) => owner.id === selectedOwnerId);

    if (!selectedOwner) {
      setOwnerError('Please select an owner.');
      setOwner(null);
      return;
    }

    const ownerData: OwnerData = {
      id: selectedOwner.id,
      name: selectedOwner.full_name,
      email: selectedOwner.email,
    };

    setOwner(ownerData);
    setOwnerError(null);

    if (!id || !currentUser?.tenantId) {
      setError('Invalid ID or tenant ID not found.');
      setOwnerError('Please select an owner.');
      return;
    }

    const [platform, ...uidParts] = id.split('-');
    if (!platform || !uidParts.length) {
      setError('Invalid ID format. Expected format: platform-uid');
      setOwnerError('Please select an owner.');
      return;
    }
    const uid = uidParts.join('-');
    const tenantRef = doc(db, 'tenants', currentUser.tenantId!);
    const platformDocName = platform.toLowerCase();
    const accessReviewRef = doc(tenantRef, 'access_review', platformDocName);

    try {
      await updateDoc(accessReviewRef, {
        [`members.${uid}.owner`]: ownerData,
        [`members.${uid}.updatedAt`]: Timestamp.now(),
      });
      showSuccessToast({ message: 'Owner updated successfully' });
    } catch (error) {
      setError('Failed to update owner.');
      setOwnerError('Failed to update owner.');
      showErrorToast({ message: 'Failed to update owner.' });
    }
  };

  const handleCloseDrawer = () => {
    onClose();
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  // Helper function to validate reviewer data
  const isValidReviewer = (reviewer: any): boolean => {
    return reviewer && 
           typeof reviewer === 'object' && 
           reviewer.id && 
           reviewer.name && 
           reviewer.email &&
           typeof reviewer.name === 'string' && 
           reviewer.name.trim().length > 0;
  };

  // Helper function to validate status values
  const isValidStatus = (status: any): boolean => {
    const validStatuses = [
      'Active', 'Inactive', 'Deactivated', 'Pending', 
      'Due', 'Completed', 'In review', 'Not Started', 
      'Approved', 'Denied', 'Connected', 'Invited'
    ];
    return status && typeof status === 'string' && validStatuses.includes(status);
  };

  // Helper function to validate accountRole data
  const isValidAccountRole = (role: any): boolean => {
    return role && typeof role === 'string' && role.trim().length > 0;
  };

  if (loading) {
    return (
      <Drawer open={isOpen} onClose={handleCloseDrawer} direction="right" className="" size="45%">
        <DrawerHeader title="Account Details" onClose={handleCloseDrawer} />
        <div
          className="p-6 d-flex justify-content-center align-items-center"
          style={{ height: 'calc(100vh - 30px)' }}
        >
          <span className="spinner-border spinner-border-sm"></span>
          <span className="ms-2">Loading...</span>
        </div>
      </Drawer>
    );
  }

  if (!accessReview) {
    return (
      <Drawer open={isOpen} onClose={handleCloseDrawer} direction="right" className="" size="45%">
        <DrawerHeader title="Account Details" onClose={handleCloseDrawer} />
        <div className="p-6 text-center text-gray-600" style={{ height: 'calc(100vh - 30px)' }}>
          {error || 'No access review data found for this user.'}
        </div>
      </Drawer>
    );
  }

  const filteredReviews = owner
    ? reviews.filter(
        (review) =>
          isValidReviewer(review.reviewer) &&
          review.reviewer.id === owner.id &&
          review.reviewer.name === owner.name &&
          review.reviewer.email === owner.email
      )
    .sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      const getTime = (val: any) => {
        if (val && typeof val.toDate === 'function') {
          return val.toDate().getTime();
        } else if (typeof val === 'string' || typeof val === 'number' || val instanceof Date) {
          return new Date(val).getTime();
        }
        return 0;
      };
      const aTime = getTime(a.createdAt);
      const bTime = getTime(b.createdAt);
      return bTime - aTime;
    })
  : [];

  return (
    <Drawer open={isOpen} onClose={handleCloseDrawer} direction="right" className="" size="45%">
      <DrawerHeader title="Account Details" onClose={handleCloseDrawer} />
      <div
        className="scroll-y overflow-auto p-6 mb-4"
        tabIndex={-1}
        style={{ height: 'calc(100vh - 60px)' }}
      >
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3">
            <label className="text-gray-800 fw-semibold me-2 fs-6">Account</label>
          </div>
          <div className="col-9">
            <span className="fw-normal fs-6">{accessReview.name}</span>
          </div>
        </div>

        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3 d-flex align-items-center">
            <label className="text-gray-800 fw-semibold me-2 fs-6">Vendor</label>
          </div>
          <div className="col-9">
            <span className="fw-normal fs-6">{accessReview.platform}</span>
          </div>
        </div>
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3 d-flex align-items-center">
            <label className="text-gray-800 fw-semibold me-2 fs-6">Owner</label>
          </div>
          <div className="col-9">
            <select
              name="group"
              className={`form-select py-2 px-4 ${ownerError ? 'is-invalid' : ''}`}
              data-control="select2"
              data-placeholder="Select Owner"
              onChange={handleOwnerChange}
              value={owner?.id || ''}
              disabled={currentUser?.role === 'Auditor'}
              style={{ width: '100%', height: '38px' }}
            >
              <option value="" disabled>
                Select Owner
              </option>
              {owners
                .filter((user) => user.role === 'Admin' || user.role === 'Owner')
                .map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.full_name}
                  </option>
                ))}
            </select>
            {ownerError && <div className="text-danger fs-7 mt-2">{ownerError}</div>}
          </div>
        </div>
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3 d-flex align-items-center">
            <label className="text-gray-800 fw-semibold me-2 fs-6">Status</label>
          </div>
          <div className="col-9">
            <StatusBadge
              status={
                (isValidStatus(accessReview?.status) &&
                  (accessReview.status!.charAt(0).toUpperCase() + accessReview.status!.slice(1)) as any) ||
                  'Active'
              }
            />
          </div>
        </div>
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3 d-flex align-items-center">
            <label className="text-gray-800 fw-semibold me-2 fs-6">Role</label>
          </div>
          <div className="col-9">
            {isValidAccountRole(accessReview?.role) ? (
              <span className="badge px-3 rounded-pill me-1" style={{ border: '1px solid #E1E1E1' }}>
                {accessReview.role!.charAt(0).toUpperCase() + accessReview.role!.slice(1)}
              </span>
            ) : (
              '--'
            )}
          </div>
        </div>
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3 d-flex align-items-center">
            <label className="text-gray-800 fw-semibold me-2 fs-6">2FA</label>
          </div>
          <div className="col-9">
            <span className="fw-normal fs-6">
              {accessReview.twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3 d-flex align-items-center">
            <label className="text-gray-800 fw-semibold me-2 fs-6">Created on</label>
          </div>
          <div className="col-9">
            <span className="fw-normal fs-6">
              {accessReview.createdAt
                ? new Date(accessReview.createdAt)
                    .toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                    .replace(/ (\d{4})$/, ', $1')
                : '--'}
            </span>
          </div>
        </div>
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-3 d-flex align-items-center">
            <label className="text-gray-800 fw-semibold me-2 fs-6">Deactivated on</label>
          </div>
          <div className="col-9">
            <span className="fw-normal fs-6">
              {accessReview.deactivatedOn
                ? new Date(accessReview.deactivatedOn)
                    .toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                    .replace(/ (\d{4})$/, ', $1')
                : '--'}
            </span>
          </div>
        </div>
        <div className="d-flex h-55px border-bottom">
          <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-semibold flex-nowrap">
            <li className="nav-item">
              <Link
                to="/access"
                className={`nav-link text-gray-500 me-6 ${
                  activeTab === 'reviews' ? 'text-active-dark active' : ''
                }`}
                onClick={() => handleTabClick('reviews')}
              >
                Access Reviews
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/access"
                className={`nav-link text-gray-500 me-6 ${
                  activeTab === 'access' ? 'text-active-dark active' : ''
                }`}
                onClick={() => handleTabClick('access')}
              >
                Access
              </Link>
            </li>
          </ul>
        </div>
        {activeTab === 'reviews' && (
          <div className="mt-8">
            {reviewsError ? (
              <div className="p-6 text-center text-gray-600">{reviewsError}</div>
            ) : filteredReviews.length > 0 ? (
              filteredReviews.map((review, index) => (
                <div key={index} className="card mb-4 border rounded-3">
                  <div className="card-body" style={{ padding: '20px' }}>
                    <h4 className="fw-semibold fs-6 text-gray-900 mb-1">{review.reviewName}</h4>
                    <div className="d-flex align-items-center mb-0">
                      <div className="text-gray-900 fw-normal me-2">Reviewed by:</div>
                      <div className="d-flex align-items-center">
                        <span className="fw-medium me-2">
                          {isValidReviewer(review.reviewer) ? (
                            <OwnerDisplay owner={review.reviewer.name} />
                          ) : (
                            <span>--</span>
                          )}
                        </span> |
                        <span className="ms-2">
                          {isValidStatus(review.integrationStatuses) ? (
                            <StatusBadge
                              status={
                                review.integrationStatuses as
                                  | 'Active'
                                  | 'Inactive'
                                  | 'Deactivated'
                                  | 'Pending'
                                  | 'Due'
                                  | 'Completed'
                                  | 'In review'
                              }
                            />
                          ) : (
                            <span>--</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="text-gray-700 fs-7">
                      {review.createdAt ? FormatTimestamp({ timestamp: review.createdAt }) : '--'}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-20">
                <div
                  className="d-flex align-items-center justify-content-center mb-4 rounded-3"
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-clipboard-list"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
                    <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
                    <path d="M9 12l.01 0" />
                    <path d="M13 12l2 0" />
                    <path d="M9 16l.01 0" />
                    <path d="M13 16l2 0" />
                  </svg>
                </div>
                <div className="fs-6">
                  <p className="fw-semibold">No access reviews assigned to this owner</p>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'access' && (
          <div className="mt-8">
            {platformsError ? (
              <div className="p-6 text-center text-gray-600">{platformsError}</div>
            ) : linkedPlatforms.length > 0 ? (
              linkedPlatforms.map((platformData, index) => (
                <div key={index} className="card mb-4 border rounded-3">
                  <div className="card-body" style={{ padding: '20px' }}>
                    <h4 className="fw-semibold fs-6 text-gray-900 mb-1">
                      {platformData.platform && typeof platformData.platform === 'string' && platformData.platform.trim().length > 0
                        ? platformData.platform.charAt(0).toUpperCase() + platformData.platform.slice(1)
                        : '--'
                      }
                    </h4>
                    <div className="d-flex align-items-center mb-0">
                      <div className="text-gray-900 fw-normal me-2">Linked via:</div>
                      <div className="d-flex align-items-center">
                        <span className="fw-medium me-2">SecureSlate</span> |
                        <span className="ms-2">
                          {isValidStatus(platformData.status) ? (
                            <StatusBadge
                              status={
                                platformData.status as
                                  | 'Active'
                                  | 'Inactive'
                                  | 'Deactivated'
                                  | 'Pending'
                                  | 'Due'
                                  | 'Completed'
                                  | 'In review'
                              }
                            />
                          ) : (
                            <span>--</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="text-gray-700 fs-7">
                      {platformData.createdAt ? FormatTimestamp({ timestamp: platformData.createdAt }) : '--'}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-20">
                <div
                  className="d-flex align-items-center justify-content-center mb-4 rounded-3"
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-clipboard-list"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
                    <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
                    <path d="M9 12l.01 0" />
                    <path d="M13 12l2 0" />
                    <path d="M9 16l.01 0" />
                    <path d="M13 16l2 0" />
                  </svg>
                </div>
                <div className="fs-6">
                  <p className="fw-semibold">No platforms linked to this owner</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ViewUser;